import { BoxWrapper, Checkbox, InputField, Select, InlineErrorView, RadioInputField } from '@common/components'
import { BuyFormData } from '@buy/state/buy-form/state'

import { Field, FieldProps, FormikProps } from 'formik'
import React, { FunctionComponent, useCallback, useEffect, useState, ChangeEvent, useRef } from 'react'
import { isEmpty } from 'lodash-es'
import { manuallyValidateFieldData, checkAlphaNumerical } from '@buy/utils'
import { connectRedux, GoToComponentProps } from '@common/utils'
import { BuyBillingFormInfoState } from '@buy/connectedComponents/BillingFormInfo/state'
import {
    BuyBillingFormInfoActionsType,
    mapBuyBillingFormInfoActions,
    mapBuyBillingFormInfoState,
} from '@buy/connectedComponents/BillingFormInfo/connector'
import { Address } from '@common/state/location/state'
import { CHECKOUTFORM_FIELDS } from '@buy/constants'
import { TAX_COUNTRIES } from '@common/constants'
import selectStyles from '@common/components/Select/select.module.css'
import InfoIcon from '@common/images/icon-info-grey.svg'
import { getAddressFromGooglePlace } from '@common/utils/getAddressFromGooglePlace'
import { useAddressAutoFill } from '@common/hooks/useAddressAutoFill'
import { GooglePlace } from '@common/state/addressAutoFill/state'
import { useTaxNumberValidation } from '@buy/hooks'
import { getProductSettingsData } from '@buy/state/selectors'
import { useSelector } from 'react-redux'
import { flushSync } from 'react-dom'
import billingStyles from './billing-info.module.css'
import { CountryCodeInfo } from '../CheckoutFormContainer'

export interface BuyBillingFormInfoComponentProps {
    useSameForBilling: boolean
    resetFatalError: () => void
    canValidateAddress: (values: BuyFormData) => void
    setUseSameForBilling: (useSame: boolean) => void
    trackBillingInfoStep: () => void
    setHasAddressValidationErrors: (value: React.SetStateAction<boolean>) => void
    formikRef: React.RefObject<FormikProps<BuyFormData>>
    setCheckoutFormInlineError: (value: string) => void
    removeCheckoutFormInlineError: (value: string) => void
    inlineErrors: { [key: string]: string } | Record<string, unknown>
    checkCurrencyConflict: (country: string, originCountry: string) => void
    setTaxRequired: (value: boolean) => void
    countryCodeInfo: CountryCodeInfo
    setCountryCodeInfo: React.Dispatch<React.SetStateAction<CountryCodeInfo>>
    setIsDisabled: (value: boolean) => void
}

type BuyBillingFormInfoProps = GoToComponentProps<
    BuyBillingFormInfoState,
    BuyBillingFormInfoActionsType,
    BuyBillingFormInfoComponentProps
>

export const BillingFormInfo: FunctionComponent<BuyBillingFormInfoProps> = ({ state, actions, props }) => {
    const { content, existingBillingInformation, isUserLoggedIn, location, taxType } = state
    const {
        useSameForBilling,
        resetFatalError,
        canValidateAddress,
        setUseSameForBilling,
        trackBillingInfoStep,
        setHasAddressValidationErrors,
        formikRef,
        setCheckoutFormInlineError,
        removeCheckoutFormInlineError,
        inlineErrors,
        checkCurrencyConflict,
        setTaxRequired,
        countryCodeInfo,
        setCountryCodeInfo,
        setIsDisabled,
    } = props

    const productSettings = useSelector(getProductSettingsData)
    const [showTaxField, setShowTaxField] = useState(false)
    const [showIndividualTax, setShowIndividualTax] = useState(true)
    const [showBrazilTaxField, setShowBrazilTaxField] = useState(false)
    const [showOptionalTaxField, setShowOptionalTaxField] = useState(false)
    const [shouldLoadAddressAutoFill, setShouldLoadAddressAutoFill] = useState(false)
    const [isTaxValidationComplete, setIsTaxValidationComplete] = useState(false)

    const taxNumberValidation = useTaxNumberValidation(setIsDisabled)

    useEffect(() => {
        if (formikRef.current && !taxNumberValidation.isValidating && (showTaxField || showBrazilTaxField)) {
            if (taxNumberValidation.isValid) {
                formikRef.current.setFieldError(CHECKOUTFORM_FIELDS.TAX_ID, '')
            } else {
                formikRef.current.setFieldError(CHECKOUTFORM_FIELDS.TAX_ID, content.errorInvalidTaxNumber)
            }
        }
    }, [
        formikRef,
        content.errorInvalidTaxNumber,
        showTaxField,
        showBrazilTaxField,
        taxNumberValidation.isValid,
        taxNumberValidation.isValidating,
        // setIsTaxNumberValid,
    ])
    useEffect(() => {
        if (isTaxValidationComplete && formikRef.current && (showTaxField || showBrazilTaxField)) {
            // Only call canValidateAddress after tax validation is complete and state has been updated
            canValidateAddress(formikRef.current.values)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTaxValidationComplete])

    // Do not set allowAddressAutofill back to true until bug WAE1-1721 is solved.
    // (See comment replies on that ticket for root cause of the bug.)
    const allowAddressAutofill = false

    const streetFieldRef = useRef<HTMLInputElement>(null)

    const selectCountry = (e: React.ChangeEvent<HTMLSelectElement>) => {
        checkCurrencyConflict(countryCodeInfo.current, e.target.value)
    }
    const selectedCountry = state.location.countries.find((country) => {
        return country.abbreviation === countryCodeInfo.current
    })

    const setAddressErrors = useCallback(
        (errorMessage: string | undefined) => {
            if (formikRef.current) {
                formikRef.current.setFieldError('CountryCode', errorMessage)
                formikRef.current.setFieldError('Street1', errorMessage)
                formikRef.current.setFieldError('City', errorMessage)
                formikRef.current.setFieldError('StateCode', errorMessage)
                formikRef.current.setFieldError('ZipCode', errorMessage)
            }
        },
        [formikRef]
    )

    const setAddressValues = useCallback(
        (address: Address) => {
            if (formikRef.current) {
                formikRef.current.setFieldValue('CountryCode', address.country)
                formikRef.current.setFieldValue('Street1', address.addressLine1)
                formikRef.current.setFieldValue('City', address.city)
                formikRef.current.setFieldValue('StateCode', address.state)
                formikRef.current.setFieldValue('ZipCode', address.postalCode)
            }
        },
        [formikRef]
    )

    const getFormValuesWithUpdatedAddress = useCallback(
        (correctedAddress: Address) => {
            if (formikRef.current) {
                const currentFormValues = formikRef.current.values
                const { addressLine1: Street1, city: City, state: StateCode, postalCode: ZipCode } = correctedAddress
                const updatedAddressValues = { ...currentFormValues, Street1, City, StateCode, ZipCode }
                return updatedAddressValues
            }
            return null
        },
        [formikRef]
    )

    const existingBillingInfoLocation = location.countries.find((country) => {
        return country.abbreviation === existingBillingInformation?.country
    })

    const getExistingBillingInfoTaxType = () => {
        if (existingBillingInformation && existingBillingInformation.federalTaxType) {
            return existingBillingInformation.federalTaxType === content.taxIndividual
                ? content.taxIndividual
                : content.taxBusiness
        }
        if (existingBillingInfoLocation) {
            return existingBillingInfoLocation.taxType === 'GST' ? content.taxCodeGST : content.taxCodeVAT
        }
        return ''
    }

    const handleTaxOptionChanged = (e: ChangeEvent<HTMLInputElement>) => {
        if (formikRef.current) {
            formikRef.current.setFieldValue('TaxNumber', '')
        }
        actions.setTaxType(e.target.value)
    }

    const handleAutoFillAddress = (place: GooglePlace) => {
        const addr = getAddressFromGooglePlace(place)
        setAddressValues(addr)
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        canValidateAddress(formikRef.current!.values)
    }

    // effects
    // get list of countries
    useEffect(() => {
        if (!location.isCountriesLoaded) {
            actions.getCountries(productSettings.productFamily)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actions, location.isCountriesLoaded])

    useEffect(() => {
        if (location.isCountriesLoaded && countryCodeInfo.current && formikRef.current) {
            if (selectedCountry && !selectedCountry.states) {
                actions.setStatesOptional(true)
            } else if (!formikRef.current.values.StateCode) {
                actions.setStatesOptional(false)
            }

            if (selectedCountry && selectedCountry.hasTaxId) {
                setShowTaxField(true)
                if (countryCodeInfo.current === 'BR') {
                    setShowBrazilTaxField(true)
                    formikRef.current.values.TaxType = 'Individual'
                    setTaxRequired(true)
                } else {
                    removeCheckoutFormInlineError('TaxNumber')
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.isCountriesLoaded, countryCodeInfo.current])

    // Once the countryCode is determined, then enable Google Places address autofill
    useEffect(() => {
        if (allowAddressAutofill && countryCodeInfo.current) {
            setShouldLoadAddressAutoFill(true)
        }
    }, [countryCodeInfo, allowAddressAutofill])

    useAddressAutoFill({
        language: state.language,
        streetFieldRef,
        countries: [countryCodeInfo.current],
        onChangePlace: handleAutoFillAddress,
        shouldLoad: shouldLoadAddressAutoFill,
    })

    // show & reset address errors
    useEffect(() => {
        if (!location.validAddress && location.addressErrors.length !== 0) {
            setHasAddressValidationErrors(true)
            setAddressErrors(' ')
        } else if (location.validAddress && location.addressErrors.length === 0) {
            setHasAddressValidationErrors(false)
            setAddressErrors(undefined)
        }
    }, [location, setAddressErrors, setHasAddressValidationErrors])

    // update with corrected address
    useEffect(() => {
        if (location.validAddress && location.correctedAddress.city && formikRef.current) {
            setAddressValues(location.correctedAddress)
            // we need to do this because, formik setFieldValue is async in nature and doesn't return a promise
            // so we can't assume we have updated values within formikRef
            const updatedFormValues = getFormValuesWithUpdatedAddress(location.correctedAddress)
            if (updatedFormValues) {
                actions.calculatePrice(updatedFormValues)
            }
        }
    }, [
        actions,
        location.validAddress,
        location.correctedAddress,
        formikRef,
        setAddressValues,
        getFormValuesWithUpdatedAddress,
    ])

    useEffect(() => {
        if (taxType) {
            if (taxType === 'individualTax' && formikRef.current) {
                formikRef.current.values.TaxType = 'Individual'
                setShowIndividualTax(true)
            }
            if (taxType === 'businessTax' && formikRef.current) {
                formikRef.current.values.TaxType = 'Business'
                setShowIndividualTax(false)
            }
            if (taxType === 'GstVatNotRequired' && formikRef.current) {
                formikRef.current.values.TaxNumber = ''
                formikRef.current.errors.TaxNumber = ''
                formikRef.current.touched.TaxNumber = false
                removeCheckoutFormInlineError('TaxNumber')
                setTaxRequired(false)
                setShowOptionalTaxField(false)
            }
            if (taxType === 'GstVatRequired') {
                setTaxRequired(true)
                setShowOptionalTaxField(true)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taxType])

    const isTaxGST = selectedCountry?.taxType === 'GST'

    const clearBillingFields = async (
        fieldName: CHECKOUTFORM_FIELDS.BILLING_FIRST_NAME | CHECKOUTFORM_FIELDS.BILLING_LAST_NAME
    ) => {
        sessionStorage.removeItem(fieldName)
        await formikRef.current?.setFieldValue(fieldName, '')
    }

    const setBillingValues = async (
        accountFieldName: CHECKOUTFORM_FIELDS.FIRST_NAME | CHECKOUTFORM_FIELDS.LAST_NAME,
        billingFieldName: CHECKOUTFORM_FIELDS.BILLING_FIRST_NAME | CHECKOUTFORM_FIELDS.BILLING_LAST_NAME
    ) => {
        const field = formikRef.current?.getFieldProps(accountFieldName)
        const form = formikRef.current
        const value = field?.value || sessionStorage.getItem(accountFieldName)

        if (!value) {
            const firstNameValue =
                formikRef.current?.values[billingFieldName] || sessionStorage.getItem(billingFieldName)
            await formikRef.current?.setFieldValue(accountFieldName, firstNameValue)

            if (firstNameValue) {
                sessionStorage.setItem(accountFieldName, firstNameValue)
            }

            if (field) {
                form?.validateField(field.name)
            }
        } else {
            await formikRef.current?.setFieldValue(billingFieldName, value || '')
            sessionStorage.setItem(billingFieldName, value || '')
        }
    }

    return (
        <BoxWrapper>
            <div className="billing-info">
                <h3>{content.billingInformation}</h3>

                {!isUserLoggedIn || isEmpty(existingBillingInformation) ? (
                    <>
                        <div className={billingStyles['checkbox-wrapper']}>
                            <Checkbox
                                checked={useSameForBilling}
                                id="useSameForBilling"
                                onchange={() => {
                                    setUseSameForBilling(!useSameForBilling)
                                    sessionStorage.setItem('useSameForBilling', `${!useSameForBilling}`)
                                    if (useSameForBilling) {
                                        clearBillingFields(CHECKOUTFORM_FIELDS.BILLING_FIRST_NAME)
                                        clearBillingFields(CHECKOUTFORM_FIELDS.BILLING_LAST_NAME)
                                    } else {
                                        setBillingValues(
                                            CHECKOUTFORM_FIELDS.FIRST_NAME,
                                            CHECKOUTFORM_FIELDS.BILLING_FIRST_NAME
                                        )
                                        setBillingValues(
                                            CHECKOUTFORM_FIELDS.LAST_NAME,
                                            CHECKOUTFORM_FIELDS.BILLING_LAST_NAME
                                        )
                                    }
                                }}
                                label={content.useAccountInfo}
                                qaTag="checkout-form-same-for-billing"
                            />
                        </div>
                        <div className={billingStyles.grid}>
                            <>
                                <Field name={CHECKOUTFORM_FIELDS.BILLING_FIRST_NAME}>
                                    {({ form, field, meta }: FieldProps) => {
                                        // TODO : optimize the conditions
                                        const billingInfoChecked = useSameForBilling
                                            ? sessionStorage.getItem(field.name)
                                            : ''

                                        let value = !field.value ? billingInfoChecked : field.value

                                        const accountFirstName = form.getFieldProps(CHECKOUTFORM_FIELDS.FIRST_NAME)
                                            .value
                                            ? sessionStorage.getItem(CHECKOUTFORM_FIELDS.BILLING_FIRST_NAME)
                                            : form.getFieldProps(CHECKOUTFORM_FIELDS.FIRST_NAME).value

                                        const checkSession = sessionStorage.getItem(CHECKOUTFORM_FIELDS.FIRST_NAME)
                                            ? sessionStorage.getItem(CHECKOUTFORM_FIELDS.FIRST_NAME)
                                            : accountFirstName

                                        value = useSameForBilling ? checkSession : value?.trim()
                                        return (
                                            <InlineErrorView
                                                fieldData={{ form, field, meta }}
                                                inlineErrors={inlineErrors}
                                                setErrorCall={setCheckoutFormInlineError}
                                                removeErrorCall={removeCheckoutFormInlineError}
                                                ignoreInlineError={useSameForBilling}
                                            >
                                                <InputField
                                                    autoComplete="cc-first-name"
                                                    disabled={useSameForBilling}
                                                    error={useSameForBilling ? undefined : meta.error}
                                                    id="name-on-card"
                                                    label={content.firstName}
                                                    name={field.name}
                                                    type="text"
                                                    valid={
                                                        useSameForBilling
                                                            ? undefined
                                                            : manuallyValidateFieldData({ form, field, meta })
                                                    }
                                                    wrapperClass={billingStyles['cc-first-name']}
                                                    value={value}
                                                    onBlur={async (e) => {
                                                        field.onBlur(e)
                                                        form.validateField(field.name)
                                                        resetFatalError()
                                                    }}
                                                    onChange={async (e) => {
                                                        flushSync(() => {
                                                            // Use flushSync to avoid issues with React 18's batching behavior
                                                            field.onChange(e)
                                                        })
                                                        await form.setFieldValue(field.name, e.target.value.trim())
                                                        sessionStorage.setItem(field.name, e.target.value.trim())
                                                        form.validateField(field.name)
                                                        resetFatalError()
                                                    }}
                                                    data-qat="billing-first-name"
                                                />
                                            </InlineErrorView>
                                        )
                                    }}
                                </Field>
                                <Field name={CHECKOUTFORM_FIELDS.BILLING_LAST_NAME}>
                                    {({ form, field, meta }: FieldProps) => {
                                        // TODO : optimize the conditions
                                        const billingInfoChecked = useSameForBilling
                                            ? sessionStorage.getItem(field.name)
                                            : ''

                                        let value = !field.value ? billingInfoChecked : field.value

                                        const accountLastName = form.getFieldProps(CHECKOUTFORM_FIELDS.LAST_NAME).value
                                            ? sessionStorage.getItem(CHECKOUTFORM_FIELDS.BILLING_LAST_NAME)
                                            : form.getFieldProps(CHECKOUTFORM_FIELDS.LAST_NAME).value

                                        const checkSession = sessionStorage.getItem(CHECKOUTFORM_FIELDS.LAST_NAME)
                                            ? sessionStorage.getItem(CHECKOUTFORM_FIELDS.LAST_NAME)
                                            : accountLastName

                                        value = useSameForBilling ? checkSession : value?.trim()
                                        return (
                                            <InlineErrorView
                                                fieldData={{ form, field, meta }}
                                                inlineErrors={inlineErrors}
                                                setErrorCall={setCheckoutFormInlineError}
                                                removeErrorCall={removeCheckoutFormInlineError}
                                                ignoreInlineError={useSameForBilling}
                                            >
                                                <InputField
                                                    autoComplete="cc-last-name"
                                                    id="name-on-card2"
                                                    disabled={useSameForBilling}
                                                    error={useSameForBilling ? undefined : meta.error}
                                                    label={content.lastName}
                                                    name={field.name}
                                                    type="text"
                                                    value={value}
                                                    valid={
                                                        useSameForBilling
                                                            ? undefined
                                                            : manuallyValidateFieldData({ form, field, meta })
                                                    }
                                                    wrapperClass={billingStyles['cc-last-name']}
                                                    onBlur={async (e) => {
                                                        field.onBlur(e)
                                                        form.validateField(field.name)
                                                        resetFatalError()
                                                    }}
                                                    onChange={async (e) => {
                                                        flushSync(() => {
                                                            // Use flushSync to avoid issues with React 18's batching behavior
                                                            field.onChange(e)
                                                        })
                                                        await form.setFieldValue(field.name, e.target.value.trim())
                                                        sessionStorage.setItem(field.name, e.target.value.trim())
                                                        form.validateField(field.name)
                                                        resetFatalError()
                                                    }}
                                                    data-qat="billing-last-name"
                                                />
                                            </InlineErrorView>
                                        )
                                    }}
                                </Field>
                            </>
                            <div className={billingStyles['country-wrapper']}>
                                <Field name={CHECKOUTFORM_FIELDS.COUNTRY_CODE}>
                                    {({ field }: FieldProps) => {
                                        return (
                                            <Select
                                                name={field.name}
                                                label={content.country}
                                                onChange={async (e) => {
                                                    selectCountry(e)
                                                    flushSync(() => {
                                                        // Use flushSync to avoid issues with React 18's batching behavior
                                                        field.onChange(e)
                                                    })
                                                    setCountryCodeInfo((oldState) => ({
                                                        ...oldState,
                                                        current: e.target.value.trim(),
                                                        previous: oldState.current,
                                                    }))
                                                    sessionStorage.setItem(field.name, e.target.value.trim())
                                                }}
                                                onBlur={() => {
                                                    // canValidateAddress(form.values)
                                                }}
                                                value={field.value as string}
                                                selected={!!countryCodeInfo.current}
                                                modifier={selectStyles['select-wrapper--compact']}
                                            >
                                                {location.countries.length === 0 ? (
                                                    <option key="countries-loading" value="">
                                                        Loading
                                                    </option>
                                                ) : (
                                                    location.countries.map((country) => (
                                                        <option
                                                            key={`country-${country.abbreviation}`}
                                                            value={country.abbreviation}
                                                        >
                                                            {country.name}
                                                        </option>
                                                    ))
                                                )}
                                            </Select>
                                        )
                                    }}
                                </Field>
                            </div>
                            <Field name={CHECKOUTFORM_FIELDS.STREET1}>
                                {({ form, field, meta }: FieldProps) => {
                                    return (
                                        <InlineErrorView
                                            fieldData={{ form, field, meta }}
                                            inlineErrors={inlineErrors}
                                            setErrorCall={setCheckoutFormInlineError}
                                            removeErrorCall={removeCheckoutFormInlineError}
                                        >
                                            <InputField
                                                // Disable Chrome's address auto complete dropdown
                                                // Note: Use "new-password" because "off" doesn't work in Chrome
                                                // See: https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion#preventing_autofilling_with_autocompletenew-password
                                                autoComplete={
                                                    shouldLoadAddressAutoFill ? 'new-password' : 'street-address'
                                                }
                                                inputRef={streetFieldRef}
                                                name={field.name}
                                                value={field.value}
                                                error={meta.error}
                                                id="street-address"
                                                label={content.address}
                                                type="text"
                                                valid={manuallyValidateFieldData({ form, field, meta })}
                                                wrapperClass={billingStyles.address}
                                                isOnChangeDebounced={true}
                                                beforeDebounceChange={() => {
                                                    setIsDisabled(true)
                                                }}
                                                onBlur={async (e) => {
                                                    field.onBlur(e)
                                                    resetFatalError()
                                                    trackBillingInfoStep()
                                                }}
                                                onChange={async (e) => {
                                                    flushSync(() => {
                                                        // Use flushSync to avoid issues with React 18's batching behavior
                                                        field.onChange(e)
                                                    })
                                                    sessionStorage.setItem(field.name, e.target.value.trim())
                                                    await form.setFieldValue(field.name, e.target.value)
                                                    setIsDisabled(false)
                                                    form.validateField(field.name)
                                                    resetFatalError()
                                                }}
                                                data-qat="billing-address"
                                            />
                                        </InlineErrorView>
                                    )
                                }}
                            </Field>

                            <Field name={CHECKOUTFORM_FIELDS.CITY}>
                                {({ field, form, meta }: FieldProps) => {
                                    return (
                                        <InlineErrorView
                                            fieldData={{ form, field, meta }}
                                            inlineErrors={inlineErrors}
                                            setErrorCall={setCheckoutFormInlineError}
                                            removeErrorCall={removeCheckoutFormInlineError}
                                        >
                                            <InputField
                                                error={meta.error}
                                                name={field.name}
                                                // Disable Chrome's address auto complete dropdown
                                                // Note: Use "new-password" because "off" doesn't work in Chrome
                                                // See: https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion#preventing_autofilling_with_autocompletenew-password
                                                autoComplete={
                                                    shouldLoadAddressAutoFill ? 'new-password' : 'address-level2'
                                                }
                                                id="city"
                                                label={content.city}
                                                type="text"
                                                value={field.value}
                                                valid={manuallyValidateFieldData({ field, form, meta })}
                                                wrapperClass={billingStyles.city}
                                                isOnChangeDebounced={true}
                                                beforeDebounceChange={() => {
                                                    setIsDisabled(true)
                                                }}
                                                onBlur={async (e) => {
                                                    field.onBlur(e)
                                                    resetFatalError()
                                                }}
                                                onChange={async (e) => {
                                                    flushSync(() => {
                                                        // Use flushSync to avoid issues with React 18's batching behavior
                                                        field.onChange(e)
                                                    })
                                                    sessionStorage.setItem(field.name, e.target.value.trim())
                                                    await form.setFieldValue(field.name, e.target.value)
                                                    setIsDisabled(false)
                                                    form.validateField(field.name)
                                                    resetFatalError()
                                                }}
                                                data-qat="billing-city"
                                            />
                                        </InlineErrorView>
                                    )
                                }}
                            </Field>
                            {selectedCountry && selectedCountry.states && (
                                <Field name={CHECKOUTFORM_FIELDS.STATE_CODE}>
                                    {({ form, field, meta }: FieldProps) => {
                                        return (
                                            <InlineErrorView
                                                fieldData={{ form, field, meta }}
                                                inlineErrors={inlineErrors}
                                                setErrorCall={setCheckoutFormInlineError}
                                                removeErrorCall={removeCheckoutFormInlineError}
                                            >
                                                <Select
                                                    error={meta.error}
                                                    name={field.name}
                                                    value={field.value}
                                                    label={content.state}
                                                    onChange={async (e) => {
                                                        flushSync(() => {
                                                            // Use flushSync to avoid issues with React 18's batching behavior
                                                            field.onChange(e)
                                                        })
                                                        sessionStorage.setItem(field.name, e.target.value.trim())
                                                        await form.setFieldValue(field.name, e.target.value)
                                                        resetFatalError()
                                                    }}
                                                    onBlur={async (e) => {
                                                        field.onBlur(e)
                                                        form.validateField(field.name)
                                                        resetFatalError()
                                                    }}
                                                    valid={manuallyValidateFieldData({ form, field, meta })}
                                                    wrapperClass={billingStyles.state}
                                                    modifier={selectStyles['select-wrapper--compact']}
                                                >
                                                    <option>{content.pleaseSelect}</option>
                                                    {selectedCountry.states &&
                                                        selectedCountry.states.map((countryState) => (
                                                            <option
                                                                key={`state-${countryState.code}`}
                                                                value={countryState.code}
                                                            >
                                                                {countryState.name}
                                                            </option>
                                                        ))}
                                                </Select>
                                            </InlineErrorView>
                                        )
                                    }}
                                </Field>
                            )}
                            <Field name={CHECKOUTFORM_FIELDS.ZIP_CODE}>
                                {({ form, field, meta }: FieldProps) => {
                                    return (
                                        <InlineErrorView
                                            fieldData={{ form, field, meta }}
                                            inlineErrors={inlineErrors}
                                            setErrorCall={setCheckoutFormInlineError}
                                            removeErrorCall={removeCheckoutFormInlineError}
                                        >
                                            <InputField
                                                error={meta.error}
                                                name={field.name}
                                                // Disable Chrome's address auto complete dropdown
                                                // Note: Use "new-password" because "off" doesn't work in Chrome
                                                // See: https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion#preventing_autofilling_with_autocompletenew-password
                                                autoComplete={
                                                    shouldLoadAddressAutoFill ? 'new-password' : 'postal-code'
                                                }
                                                id="zipcode"
                                                label={content.postalCode}
                                                type="text"
                                                valid={manuallyValidateFieldData({ form, field, meta })}
                                                wrapperClass={billingStyles.zipcode}
                                                value={field.value}
                                                isOnChangeDebounced={true}
                                                beforeDebounceChange={() => {
                                                    setIsDisabled(true)
                                                }}
                                                onBlur={async (e) => {
                                                    field.onBlur(e)
                                                    resetFatalError()
                                                }}
                                                onChange={async (e) => {
                                                    flushSync(() => {
                                                        // Use flushSync to avoid issues with React 18's batching behavior
                                                        field.onChange(e)
                                                    })
                                                    sessionStorage.setItem(field.name, e.target.value.trim())
                                                    await form.setFieldValue(field.name, e.target.value)
                                                    setIsDisabled(false)
                                                    form.validateField(field.name)
                                                    resetFatalError()
                                                }}
                                                data-qat="billing-postal-code"
                                            />
                                        </InlineErrorView>
                                    )
                                }}
                            </Field>
                            <Field name={CHECKOUTFORM_FIELDS.CONTACT_PHONE}>
                                {({ form, field, meta }: FieldProps) => {
                                    return (
                                        <InlineErrorView
                                            fieldData={{ form, field, meta }}
                                            inlineErrors={inlineErrors}
                                            setErrorCall={setCheckoutFormInlineError}
                                            removeErrorCall={removeCheckoutFormInlineError}
                                        >
                                            <InputField
                                                error={meta.error}
                                                name={field.name}
                                                id="contact-number"
                                                label={content.phone}
                                                type="tel"
                                                value={field.value}
                                                valid={manuallyValidateFieldData({ form, field, meta })}
                                                wrapperClass={billingStyles.phone}
                                                isOnChangeDebounced={true}
                                                beforeDebounceChange={() => {
                                                    setIsDisabled(true)
                                                }}
                                                onBlur={async (e) => {
                                                    await form.setFieldValue(field.name, e.target.value.trim())
                                                    field.onBlur(e)
                                                    resetFatalError()
                                                }}
                                                onChange={(e) => {
                                                    flushSync(() => {
                                                        // Use flushSync to avoid issues with React 18's batching behavior
                                                        field.onChange(e)
                                                    })
                                                    sessionStorage.setItem(field.name, e.target.value.trim())
                                                    setIsDisabled(false)
                                                    form.validateField(field.name)
                                                    resetFatalError()
                                                }}
                                                data-qat="billing-contact-number"
                                            />
                                        </InlineErrorView>
                                    )
                                }}
                            </Field>
                        </div>
                        {showTaxField && (
                            <Field name={CHECKOUTFORM_FIELDS.TAX_ID}>
                                {({ form, field, meta }: FieldProps) => {
                                    return (
                                        <div className={billingStyles.tax}>
                                            {showBrazilTaxField ? (
                                                <InlineErrorView
                                                    fieldData={{ form, field, meta }}
                                                    inlineErrors={inlineErrors}
                                                    setErrorCall={setCheckoutFormInlineError}
                                                    removeErrorCall={removeCheckoutFormInlineError}
                                                >
                                                    <RadioInputField modifier={billingStyles['tax-id']}>
                                                        <input
                                                            id="individual-tax"
                                                            onChange={handleTaxOptionChanged}
                                                            value="individualTax"
                                                            checked={showIndividualTax}
                                                            name="individual-tax-id"
                                                            type="radio"
                                                        />
                                                        <label htmlFor="individual-tax">
                                                            <span>{content.taxIndividual}</span>
                                                        </label>
                                                    </RadioInputField>

                                                    <RadioInputField modifier={billingStyles['tax-id']}>
                                                        <input
                                                            id="business-tax"
                                                            onChange={handleTaxOptionChanged}
                                                            value="businessTax"
                                                            checked={!showIndividualTax}
                                                            name="business-tax-id"
                                                            type="radio"
                                                        />
                                                        <label htmlFor="business-tax">
                                                            <span>{content.taxBusiness}</span>
                                                        </label>
                                                    </RadioInputField>
                                                    <InputField
                                                        error={meta.error}
                                                        name={field.name}
                                                        id="tax-number"
                                                        label={content.taxNumber}
                                                        type="text"
                                                        value={field.value}
                                                        valid={manuallyValidateFieldData({ form, field, meta })}
                                                        isOnChangeDebounced={true}
                                                        beforeDebounceChange={() => {
                                                            setIsDisabled(true)
                                                        }}
                                                        onBlur={async (e) => {
                                                            field.onBlur(e)
                                                            resetFatalError()
                                                        }}
                                                        onChange={async (e) => {
                                                            flushSync(() => {
                                                                // Use flushSync to avoid issues with React 18's batching behavior
                                                                field.onChange(e)
                                                            })
                                                            await form.setFieldValue(field.name, e.target.value.trim())
                                                            if (!meta.error && e.target.value) {
                                                                setIsTaxValidationComplete(false)
                                                                await taxNumberValidation.validate(
                                                                    countryCodeInfo.current,
                                                                    e.target.value,
                                                                    formikRef.current!.values.TaxType
                                                                )
                                                                setIsTaxValidationComplete(true)
                                                            }
                                                            setIsDisabled(false)
                                                            resetFatalError()
                                                        }}
                                                        ismasked={true}
                                                        mask={
                                                            showIndividualTax ? '999.999.999-99' : '99.999.999/9999-99'
                                                        }
                                                        maskChar=""
                                                        data-qat="billing-tax-number"
                                                    />
                                                </InlineErrorView>
                                            ) : (
                                                <>
                                                    <Checkbox
                                                        checked={showOptionalTaxField}
                                                        id="optionalTax"
                                                        onchange={() => {
                                                            if (showOptionalTaxField && formikRef.current) {
                                                                actions.setTaxType('GstVatNotRequired')
                                                                if (formikRef.current.values) {
                                                                    actions.calculatePrice(formikRef.current.values)
                                                                }
                                                            } else {
                                                                actions.setTaxType('GstVatRequired')
                                                            }
                                                        }}
                                                        label={isTaxGST ? content.gSTOption : content.vATOption}
                                                        qaTag="checkout-form-optional-tax"
                                                        modifier={billingStyles['tax-checkbox']}
                                                    />
                                                    {showOptionalTaxField && (
                                                        <InlineErrorView
                                                            fieldData={{ form, field, meta }}
                                                            inlineErrors={inlineErrors}
                                                            setErrorCall={setCheckoutFormInlineError}
                                                            removeErrorCall={removeCheckoutFormInlineError}
                                                        >
                                                            <div className={billingStyles['tax-option']}>
                                                                <span className={billingStyles['tax-code']}>
                                                                    {TAX_COUNTRIES[countryCodeInfo.current] ||
                                                                        countryCodeInfo.current}
                                                                </span>
                                                                <InputField
                                                                    error={meta.error}
                                                                    name={field.name}
                                                                    id="tax-number"
                                                                    label={`${
                                                                        isTaxGST
                                                                            ? content.taxCodeGST
                                                                            : content.taxCodeVAT
                                                                    } ${content.taxNumber}`}
                                                                    type="text"
                                                                    maxLength={15}
                                                                    value={field.value}
                                                                    valid={manuallyValidateFieldData({
                                                                        form,
                                                                        field,
                                                                        meta,
                                                                    })}
                                                                    isOnChangeDebounced={true}
                                                                    beforeDebounceChange={() => {
                                                                        setIsDisabled(true)
                                                                    }}
                                                                    onBlur={async (e) => {
                                                                        await form.setFieldValue(
                                                                            field.name,
                                                                            e.target.value.trim()
                                                                        )
                                                                        field.onBlur(e)
                                                                        resetFatalError()
                                                                    }}
                                                                    onChange={async (e) => {
                                                                        await form.setFieldValue(
                                                                            field.name,
                                                                            e.target.value.trim()
                                                                        )
                                                                        flushSync(() => {
                                                                            // Use flushSync to avoid issues with React 18's batching behavior
                                                                            field.onChange(e)
                                                                        })
                                                                        if (!meta.error && e.target.value) {
                                                                            setIsTaxValidationComplete(false)
                                                                            await taxNumberValidation.validate(
                                                                                countryCodeInfo.current,
                                                                                e.target.value
                                                                            )
                                                                            setIsTaxValidationComplete(true)
                                                                        }
                                                                        setIsDisabled(false)
                                                                        resetFatalError()
                                                                    }}
                                                                    checkValid={checkAlphaNumerical}
                                                                    data-qat="billing-tax-number"
                                                                />
                                                            </div>
                                                            <div className={billingStyles['tax-disclaimer']}>
                                                                <img src={InfoIcon} alt="info" />
                                                                <span>
                                                                    {isTaxGST
                                                                        ? content.gSTDisclaimer
                                                                        : content.vATDisclaimer}
                                                                </span>
                                                            </div>
                                                        </InlineErrorView>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )
                                }}
                            </Field>
                        )}
                        <Field name={CHECKOUTFORM_FIELDS.COMPANY_NAME}>
                            {({ form, field, meta }: FieldProps) => {
                                return (
                                    <InlineErrorView
                                        fieldData={{ form, field, meta }}
                                        inlineErrors={inlineErrors}
                                        setErrorCall={setCheckoutFormInlineError}
                                        removeErrorCall={removeCheckoutFormInlineError}
                                    >
                                        <InputField
                                            autoComplete="company-name"
                                            name={field.name}
                                            value={field.value}
                                            error={meta.error}
                                            id="company-name"
                                            label={`${content.companyName} (${content.optional})`}
                                            type="text"
                                            valid={manuallyValidateFieldData({ form, field, meta })}
                                            wrapperClass={billingStyles['exisiting-company']}
                                            onBlur={async (e) => {
                                                await form.setFieldValue(field.name, e.target.value.trim())
                                                field.onBlur(e)
                                                form.validateField(field.name)
                                            }}
                                            onChange={(e) => {
                                                flushSync(() => {
                                                    // Use flushSync to avoid issues with React 18's batching behavior
                                                    field.onChange(e)
                                                })
                                            }}
                                            data-qat="billing-company-name"
                                        />
                                    </InlineErrorView>
                                )
                            }}
                        </Field>
                    </>
                ) : (
                    existingBillingInformation && (
                        <div className={billingStyles['existing-address']}>
                            <div>
                                {existingBillingInformation.firstName} {existingBillingInformation.lastName}
                            </div>
                            <div>{existingBillingInformation.addressLine1}</div>
                            <div>
                                {existingBillingInformation.city} {existingBillingInformation.state}{' '}
                                {existingBillingInfoLocation?.name} {existingBillingInformation.postalCode}
                            </div>
                            <div>{existingBillingInformation.phoneNumber}</div>
                            {existingBillingInformation.taxNumber && (
                                <div className={billingStyles['existing-tax']}>
                                    {getExistingBillingInfoTaxType()}: {existingBillingInformation.taxNumber}
                                </div>
                            )}
                            {existingBillingInformation.companyName ? (
                                <div>
                                    {content.companyName}: {existingBillingInformation.companyName}
                                </div>
                            ) : (
                                <Field name={CHECKOUTFORM_FIELDS.COMPANY_NAME}>
                                    {({ form, field, meta }: FieldProps) => {
                                        return (
                                            <InlineErrorView
                                                fieldData={{ form, field, meta }}
                                                inlineErrors={inlineErrors}
                                                setErrorCall={setCheckoutFormInlineError}
                                                removeErrorCall={removeCheckoutFormInlineError}
                                            >
                                                <InputField
                                                    autoComplete="company-name"
                                                    name={field.name}
                                                    value={field.value}
                                                    error={meta.error}
                                                    id="company-name"
                                                    label={`${content.companyName} (${content.optional})`}
                                                    type="text"
                                                    valid={manuallyValidateFieldData({ form, field, meta })}
                                                    wrapperClass={billingStyles['exisiting-company']}
                                                    onBlur={async (e) => {
                                                        await form.setFieldValue(field.name, e.target.value.trim())
                                                        field.onBlur(e)
                                                        form.validateField(field.name)
                                                    }}
                                                    onChange={(e) => {
                                                        flushSync(() => {
                                                            // Use flushSync to avoid issues with React 18's batching behavior
                                                            field.onChange(e)
                                                        })
                                                    }}
                                                    data-qat="billing-company-name"
                                                />
                                            </InlineErrorView>
                                        )
                                    }}
                                </Field>
                            )}
                        </div>
                    )
                )}
            </div>
        </BoxWrapper>
    )
}

export const ConnectedBuyBillingFormInfo = connectRedux(
    BillingFormInfo,
    mapBuyBillingFormInfoState,
    mapBuyBillingFormInfoActions
)
